import React, { useState, useEffect, useRef } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';

const BannerLayer = () => {
  // State Management
  const [banner, setBanner] = useState(null);
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // Current banner being viewed or edited
  const [currentBanner, setCurrentBanner] = useState({
    id: '',
    nama_banner: '',
    url_foto_banner: '',
  });

  // File state
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  // API config
  const API_BASE_URL = 'https://api.tomassage.id/api';
  const UPLOAD_URL = 'https://ygtechdev.my.id/upload';

  // Fetch banners from API
  const fetchBanners = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/banners`);
      console.log('Fetched banners:', response.data);
      
      let bannersData = [];
      
      // Handle different API response formats
      if (response.data && Array.isArray(response.data.data)) {
        bannersData = response.data.data;
      } else if (response.data && Array.isArray(response.data)) {
        bannersData = response.data;
      } else {
        throw new Error('Format data tidak valid');
      }
      
      setBanners(bannersData || []);
      
      // Set the first banner (index 0) as the active one
      if (bannersData.length > 0) {
        setBanner(bannersData[0]);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error fetching banners:', err);
      setError('Gagal memuat data banner: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchBanners();
  }, []);

  // Handle banner form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentBanner({
      ...currentBanner,
      [name]: value
    });
  };

  // Handle file selection
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  // Handle file upload
  const handleFileUpload = async () => {
    if (!selectedFile) {
      setError('Silakan pilih file terlebih dahulu');
      return;
    }

    try {
      setUploadingImage(true);
      setError(null);

      const formData = new FormData();
      formData.append('photo', selectedFile);

      const response = await axios.post(UPLOAD_URL, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data && response.data.fileUrl) {
        setCurrentBanner({
          ...currentBanner,
          url_foto_banner: response.data.fileUrl
        });
        setSelectedFile(null);
        
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        throw new Error('URL gambar tidak ditemukan dalam respon');
      }
    } catch (err) {
      console.error('Error uploading image:', err);
      setError('Gagal mengupload gambar: ' + (err.response?.data?.message || err.message));
    } finally {
      setUploadingImage(false);
    }
  };

  // Set banner for editing
  const handleEditBanner = (banner) => {
    setCurrentBanner({
      id: banner.id,
      nama_banner: banner.nama_banner,
      url_foto_banner: banner.url_foto_banner
    });
    setSelectedFile(null);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Reset banner form
  const resetBannerForm = () => {
    setCurrentBanner({
      id: '',
      nama_banner: '',
      url_foto_banner: ''
    });
    setSelectedFile(null);
    setError(null);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle add/update banner
  const handleSubmitBanner = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      
      if (!currentBanner.nama_banner || !currentBanner.url_foto_banner) {
        setError('Nama banner dan URL foto banner wajib diisi');
        return;
      }
      
      let response;
      
      // Update existing banner
      if (currentBanner.id) {
        response = await axios.put(`${API_BASE_URL}/banners/${currentBanner.id}`, currentBanner);
        setSuccessMessage('Banner berhasil diperbarui');
      } 
      // Add new banner
      else {
        response = await axios.post(`${API_BASE_URL}/banners`, currentBanner);
        setSuccessMessage('Banner baru berhasil ditambahkan');
      }
      
      // Refresh banner data
      await fetchBanners();
      
      // Close modal
      document.getElementById('bannerFormModalClose').click();
      resetBannerForm();
    } catch (err) {
      console.error('Error saving banner:', err);
      setError('Gagal menyimpan banner: ' + (err.response?.data?.message || err.message));
    }
  };

  // Handle delete banner
  const handleDeleteBanner = async (bannerId) => {
    if (!window.confirm('Apakah Anda yakin ingin menghapus banner ini?')) {
      return;
    }
    
    try {
      setError(null);
      await axios.delete(`${API_BASE_URL}/banners/${bannerId}`);
      
      // Refresh banner data
      await fetchBanners();
      
      setSuccessMessage('Banner berhasil dihapus');
    } catch (err) {
      console.error('Error deleting banner:', err);
      setError('Gagal menghapus banner: ' + (err.response?.data?.message || err.message));
    }
  };

  // Filter banners based on search term
  const filteredBanners = banners.filter(banner => {
    return banner.nama_banner?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Render loading state
  if (loading && banners.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="row mb-4">
        <div className="col-12">
          <h2 className="mb-3">Kelola Banner Promosi</h2>
          
          <div className="card shadow-sm">
            <div className="card-body p-4">
              {/* Success Message */}
              {successMessage && (
                <div className="alert alert-success alert-dismissible fade show" role="alert">
                  {successMessage}
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setSuccessMessage(null)}
                  ></button>
                </div>
              )}
              
              {/* Error Message */}
              {error && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                  {error}
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setError(null)}
                  ></button>
                </div>
              )}
              
              {/* Active Banner Display (Index 0) */}
              {banner ? (
                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 className="mb-0">Banner Aktif</h4>
                    <button 
                      className="btn btn-primary" 
                      data-bs-toggle="modal"
                      data-bs-target="#bannerFormModal"
                      onClick={() => resetBannerForm()}
                    >
                      <Icon icon="mdi:plus" className="me-1" /> Tambah Banner Baru
                    </button>
                  </div>
                  
                  <div className="card border shadow-sm">
                    <div className="card-body p-0">
                      <div className="row g-0">
                        <div className="col-md-7">
                          <img 
                            src={banner.url_foto_banner} 
                            alt={banner.nama_banner}
                            className="img-fluid rounded-start"
                            style={{ 
                              width: '100%', 
                              height: '300px', 
                              objectFit: 'cover',
                              borderTopLeftRadius: '0.375rem',
                              borderBottomLeftRadius: '0.375rem'
                            }}
                          />
                        </div>
                        <div className="col-md-5">
                          <div className="card-body h-100 d-flex flex-column">
                            <h5 className="card-title text-primary mb-3 fs-4">
                              {banner.nama_banner}
                            </h5>
                            
                            <div className="mb-3">
                              <small className="text-muted d-block mb-1">URL Gambar:</small>
                              <div className="input-group">
                                <input 
                                  type="text" 
                                  className="form-control form-control-sm bg-light" 
                                  value={banner.url_foto_banner} 
                                  readOnly
                                />
                                <button 
                                  className="btn btn-outline-secondary btn-sm" 
                                  type="button"
                                  onClick={() => navigator.clipboard.writeText(banner.url_foto_banner)}
                                  title="Salin URL"
                                >
                                  <Icon icon="mdi:content-copy" />
                                </button>
                              </div>
                            </div>
                            
                            <div className="mb-3">
                              <small className="text-muted d-block mb-1">ID Banner:</small>
                              <span className="badge bg-secondary">{banner.id}</span>
                            </div>
                            
                            {banner.created_at && (
                              <div className="mb-3">
                                <small className="text-muted d-block mb-1">Tanggal Dibuat:</small>
                                <span>{new Date(banner.created_at).toLocaleDateString('id-ID', { 
                                  year: 'numeric', month: 'long', day: 'numeric'
                                })}</span>
                              </div>
                            )}
                            
                            <div className="mt-auto d-flex gap-2">
                              <button 
                                className="btn btn-sm btn-outline-primary flex-grow-1"
                                onClick={() => {
                                  handleEditBanner(banner);
                                  document.getElementById('bannerFormModalOpenBtn').click();
                                }}
                              >
                                <Icon icon="mdi:pencil" className="me-1" /> Edit
                              </button>
                              <button 
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDeleteBanner(banner.id)}
                              >
                                <Icon icon="mdi:delete" /> Hapus
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-5 mb-4">
                  <Icon icon="mdi:image-off" style={{ fontSize: '3rem' }} className="text-secondary" />
                  <h5 className="mt-3">Belum ada banner</h5>
                  <p className="text-muted">Tambahkan banner baru untuk ditampilkan</p>
                  <button 
                    className="btn btn-primary mt-2"
                    data-bs-toggle="modal"
                    data-bs-target="#bannerFormModal"
                    onClick={resetBannerForm}
                  >
                    <Icon icon="mdi:plus" className="me-1" /> Tambah Banner
                  </button>
                </div>
              )}
              
              {/* All Banners List */}
              <div className="mt-5">
                <h4 className="mb-3">Semua Banner</h4>
                
                {/* Search */}
                <div className="row mb-4">
                  <div className="col-md-6 mb-3 mb-md-0">
                    <div className="input-group">
                      <span className="input-group-text bg-light">
                        <Icon icon="mdi:magnify" />
                      </span>
                      <input 
                        type="text" 
                        className="form-control" 
                        placeholder="Cari banner..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-md-6 text-md-end">
                    <button 
                      className="btn btn-outline-secondary" 
                      onClick={fetchBanners}
                      title="Refresh Data"
                    >
                      <Icon icon="mdi:refresh" className="me-1" /> Refresh
                    </button>
                  </div>
                </div>
                
                {/* Banner Cards */}
                <div className="row g-4">
                  {filteredBanners.length > 0 ? (
                    filteredBanners.map(bannerItem => (
                      <div key={bannerItem.id} className="col-xl-3 col-lg-4 col-md-6">
                        <div className="card h-100 border shadow-sm">
                          <img 
                            src={bannerItem.url_foto_banner} 
                            className="card-img-top" 
                            alt={bannerItem.nama_banner}
                            style={{ height: '160px', objectFit: 'cover' }}
                          />
                          <div className="card-body text-center">
                            <h5 className="card-title text-primary">{bannerItem.nama_banner}</h5>
                            <p className="card-text small text-muted">
                              ID: {bannerItem.id}
                            </p>
                            
                            <div className="d-flex justify-content-between mt-3">
                              <button 
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => {
                                  handleEditBanner(bannerItem);
                                  document.getElementById('bannerFormModalOpenBtn').click();
                                }}
                              >
                                <Icon icon="mdi:pencil" className="me-1" /> Edit
                              </button>
                              <button 
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDeleteBanner(bannerItem.id)}
                              >
                                <Icon icon="mdi:delete" /> Hapus
                              </button>
                            </div>
                          </div>
                          {banner && banner.id === bannerItem.id && (
                            <div className="card-footer bg-primary text-white">
                              <small><Icon icon="mdi:check-circle" className="me-1" /> Banner Aktif</small>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="col-12 text-center py-5">
                      <Icon icon="mdi:image-off" style={{ fontSize: '3rem' }} className="text-secondary" />
                      <h5 className="mt-3">Tidak ada banner yang ditemukan</h5>
                      <p className="text-muted">Coba ubah kata kunci pencarian Anda</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Hidden button to open modal programmatically */}
      <button 
        id="bannerFormModalOpenBtn" 
        data-bs-toggle="modal" 
        data-bs-target="#bannerFormModal" 
        style={{ display: 'none' }}
      ></button>
      
      {/* Banner Form Modal */}
      <div className="modal fade" id="bannerFormModal" tabIndex="-1" aria-hidden="true">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {currentBanner.id ? 'Edit Banner' : 'Tambah Banner Baru'}
              </h5>
              <button 
                type="button" 
                className="btn-close" 
                data-bs-dismiss="modal" 
                aria-label="Close"
                id="bannerFormModalClose"
              ></button>
            </div>
            <form onSubmit={handleSubmitBanner}>
              <div className="modal-body">
                {error && (
                  <div className="alert alert-danger">
                    {error}
                  </div>
                )}
                
                <div className="mb-3">
                  <label className="form-label">Nama Banner <span className="text-danger">*</span></label>
                  <input 
                    type="text"
                    className="form-control"
                    name="nama_banner"
                    placeholder="Contoh: Promo Ramadhan"
                    value={currentBanner.nama_banner}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div className="mb-3">
                  <label className="form-label">Upload Foto Banner</label>
                  <div className="input-group mb-3">
                    <input 
                      type="file" 
                      className="form-control" 
                      accept="image/*"
                      onChange={handleFileChange}
                      ref={fileInputRef}
                    />
                    <button 
                      className="btn btn-outline-primary" 
                      type="button"
                      onClick={handleFileUpload}
                      disabled={!selectedFile || uploadingImage}
                    >
                      {uploadingImage ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Icon icon="mdi:cloud-upload" className="me-1" /> Upload
                        </>
                      )}
                    </button>
                  </div>
                  <small className="text-muted">
                    Upload gambar banner Anda (format: JPG, PNG, dll)
                  </small>
                </div>
                
                <div className="mb-3">
                  <label className="form-label">URL Foto Banner <span className="text-danger">*</span></label>
                  <input 
                    type="url"
                    className="form-control"
                    name="url_foto_banner"
                    placeholder="https://example.com/images/banner.jpg"
                    value={currentBanner.url_foto_banner}
                    onChange={handleInputChange}
                    required
                  />
                  <small className="text-muted">
                    URL akan otomatis terisi setelah upload gambar, atau masukkan URL secara manual
                  </small>
                </div>
                
                {currentBanner.url_foto_banner && (
                  <div className="mb-3">
                    <label className="form-label">Preview:</label>
                    <div className="border rounded text-center p-2">
                      <img 
                        src={currentBanner.url_foto_banner} 
                        alt="Preview"
                        className="img-fluid rounded"
                        style={{ maxHeight: '200px' }}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://via.placeholder.com/640x320?text=Invalid+Image+URL';
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  data-bs-dismiss="modal"
                >
                  Batal
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={uploadingImage}
                >
                  {currentBanner.id ? 'Perbarui' : 'Simpan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default BannerLayer;